import React from 'react';
import { useFormikContext } from 'formik';
import { SectionCard } from '../../../components/SectionCard';
import { useDeliveryProfile } from '../../../components/DeliveryProfileEditor';
import { useSettings } from '../../../hooks/useSettings';
import { InlineShippingOptions, ProductInput, ProductType, ShippingCountryOfOriginOption as ShippingCountryOfOriginOptionType } from '@shopify-multiseller/graphql/partner/types';
import { RadioRows, type RadioRowOption } from '../../../components/RadioRows';
import { Badge } from '../../../components/primitives';
import { Button } from '../../../components/primitives';
import iso3166 from 'iso3166';
import { useAuth } from '../../../hooks/useAuth';
import { useConfig } from '../../../hooks/useConfig';
import { FormSelect, FormInput } from '../../../components/form';
import { Checkbox } from '../../../components/primitives';
import { formatCurrency } from '../../../helpers/formatters';
import { IProductInitialValuesProps } from '../types';
import {  resolveInitialStandardShipping } from '../utils';

interface ProductShippingSectionProps {
  disabled?: boolean;
}

type OptionItem = { label: string; value: string };

const DeliveryProfileLoading = () => (
  <div className="flex items-center space-x-2 animate-pulse">
    <div className="w-24 h-4 bg-muted rounded"></div>
    <div className="w-16 h-5 bg-muted rounded"></div>
  </div>
);

const DeliveryProfileApplied: React.FC<{
  name: string | null | undefined;
  onChoose: () => void;
  onViewEdit: () => void;
  disabled?: boolean;
}> = ({ onChoose, onViewEdit, disabled }) => (
  <div className="flex items-center space-x-2">
    {!disabled && (
      <>
        <Button variant="ghost" size="sm" onClick={onChoose}>Change</Button>
        <Button variant="outline" size="sm" onClick={onViewEdit}>View/Edit</Button>
      </>
    )}
  </div>
);

const DeliveryProfileNotSet: React.FC<{
  onChoose: () => void;
  onCreate: () => void;
}> = ({ onChoose, onCreate }) => (
  <div className="flex items-center space-x-2">
    <Button variant="ghost" size="sm" onClick={onChoose}>Choose</Button>
    <Button variant="outline" size="sm" onClick={onCreate}>Add</Button>
  </div>
);

/**
 * What the fixed-rate option currently amounts to, in one line. An unselected
 * row still shows it, so switching back is a known quantity rather than a
 * click into the unknown.
 */
const fixedRateSummary = (
  standardShipping: { domesticRate?: number | null; internationalRate?: number | null } | null | undefined,
): string | undefined => {
  const domestic = standardShipping?.domesticRate;
  const international = standardShipping?.internationalRate;
  if (domestic == null && international == null) return undefined;

  const parts: string[] = [];
  if (domestic != null) parts.push(`${formatCurrency({ amount: domestic })} domestic`);
  if (international != null) parts.push(`${formatCurrency({ amount: international })} international`);
  return parts.join(' · ');
};

const ShippingCountryOfOriginOption: React.FC<{ disabled: boolean }> = ({ disabled }) => {
  const auth = useAuth();
  const config = useConfig();
  const settings = useSettings();
  const { setFieldValue } = useFormikContext<ProductInput>();
  const { shippingCountryOfOriginOption, customShippingCountryOfOriginList, hideSelectCountryIfSingleCountry } = settings.productShipping;

  let countryOptions: OptionItem[] = Object.entries(iso3166).map(([key, value]) => ({
    label: value.name,
    value: key
  }));

  // Determine if we have a single country situation
  let isSingleCountry = false;
  let singleCountryValue = '';

  switch (shippingCountryOfOriginOption) {
    case ShippingCountryOfOriginOptionType.CustomList:
      countryOptions = countryOptions.filter(country => customShippingCountryOfOriginList.includes(country.value));
      isSingleCountry = countryOptions.length === 1;
      if (isSingleCountry) singleCountryValue = countryOptions[0].value;
      break;

    case ShippingCountryOfOriginOptionType.ShopCountryOnly:
      singleCountryValue = config?.shopConfig?.shop?.countryCode || '';
      isSingleCountry = true;
      countryOptions = countryOptions.filter(country => country.value === singleCountryValue);
      break;

    case ShippingCountryOfOriginOptionType.SellerCountryOnly:
      singleCountryValue = auth?.user?.seller?.address?.countryCode || '';
      isSingleCountry = true;
      countryOptions = countryOptions.filter(country => country.value === singleCountryValue);
      break;

    case ShippingCountryOfOriginOptionType.AllCountries:
      isSingleCountry = false;
      break;
  }



  // If we should hide the select and we have a single country
  React.useEffect(() => {
    if (hideSelectCountryIfSingleCountry && isSingleCountry) {
      setFieldValue('standardShipping.countryOfOrigin', singleCountryValue);
    }
  }, [hideSelectCountryIfSingleCountry, isSingleCountry, singleCountryValue, setFieldValue]);

  // Hidden entirely when the shop has fixed it to one country — a select with
  // a single option is a label pretending to be a control.
  if (hideSelectCountryIfSingleCountry && isSingleCountry) return null;

  return (
    <FormSelect
      name="standardShipping.countryOfOrigin"
      label="Country of origin"
      options={countryOptions}
      disabled={disabled}
    />
  );
};

interface ShippingRateFieldsProps {
  disabled: boolean;
  inlineShippingOptions: InlineShippingOptions;
  isInternationalShipping: boolean;
  onChangeInternationalShipping: (_value: boolean) => void;
}

const ShippingRateFields = React.memo(({
  disabled,
  inlineShippingOptions,
  isInternationalShipping,
  onChangeInternationalShipping,
}: ShippingRateFieldsProps) => {
  const showInternational =
    inlineShippingOptions === InlineShippingOptions.InternationalMandatory ||
    (inlineShippingOptions === InlineShippingOptions.InternationalOptional && isInternationalShipping);

  return (
    /*
      One row of the three facts a rate is made of — where it ships from and
      what it costs at home and abroad. This was a four-column grid holding one
      narrow input, two empty spacer columns and a pair of horizontal rules,
      so the fields sat at different heights with a line drawn between them.
    */
    <div className="space-y-3">
      <div className="grid gap-4 sm:grid-cols-3">
        <ShippingCountryOfOriginOption disabled={disabled} />
        <FormInput
          money
          name="standardShipping.domesticRate"
          label="Domestic rate"
          prefix="$"
          disabled={disabled}
        />
        {showInternational && (
          <FormInput
            money
            name="standardShipping.internationalRate"
            label="International rate"
            prefix="$"
            disabled={disabled}
          />
        )}
      </div>

      {inlineShippingOptions === InlineShippingOptions.InternationalOptional && (
        <div className="flex items-center gap-2">
          <Checkbox
            id="ship-internationally"
            checked={isInternationalShipping}
            onCheckedChange={() => onChangeInternationalShipping(!isInternationalShipping)}
            disabled={disabled}
          />
          <label
            htmlFor="ship-internationally"
            className="cursor-pointer select-none text-sm text-foreground"
          >
            Also ship internationally
          </label>
        </div>
      )}
    </div>
  );
});
ShippingRateFields.displayName = 'ShippingRateFields';

export const ProductShippingSection: React.FC<ProductShippingSectionProps> = ({ disabled = false }) => {
  // All hooks must be called before any conditional returns
  const { values, setFieldValue } = useFormikContext<IProductInitialValuesProps>();
  const settings = useSettings();
  const config = useConfig();
  const auth = useAuth();
  const deliveryProfile = useDeliveryProfile();
  // Derived, not mirrored: shippingType is the source of truth, with the
  // hydration fallback (a stored profile id / manual rates) for forms loaded
  // before shippingType is set.
  const selectedShippingMethod: string | undefined =
    values.shippingType === 'profile' ? 'profile'
    : values.shippingType === 'standard' ? 'manual'
    : values.deliveryProfileId ? 'profile'
    : values.standardShipping ? 'manual'
    : undefined;
  const [isInternationalShipping, setIsInternationalShipping] = React.useState(
    () => values.standardShipping?.internationalRate != null,
  );

  React.useEffect(() => {
    if (values.productType === ProductType.Physical) {
      if (values.shippingType === 'standard') {
        !values.standardShipping && setFieldValue('standardShipping', resolveInitialStandardShipping(settings.productShipping, config?.shopConfig?.shop.countryCode, auth?.user?.seller?.address?.countryCode));
      } else {
        setFieldValue('standardShipping', undefined);
      }
    }
  }, [values.shippingType, values.productType]);

  React.useEffect(() => {
    if (values.productType === ProductType.Digital) {
      setFieldValue('shippingType', undefined);
    } else if (values.productType === ProductType.Physical) {
      // Only set default shipping type if it's not already set
      if (!values.shippingType) {
        setFieldValue('shippingType', 'standard');
      }
      
      // Set standard shipping defaults if needed
      if (values.shippingType === 'standard' && !values.standardShipping) {
        setFieldValue('standardShipping', resolveInitialStandardShipping(
          settings.productShipping, 
          config?.shopConfig?.shop.countryCode, 
          auth?.user?.seller?.address?.countryCode
        ));
      }
    }
  }, [values.productType, settings.productShipping]);

  // All useCallback hooks
  const handleShippingMethodChange = React.useCallback((value: string) => {
    if (value === 'profile') {
      setFieldValue('standardShipping', null);
      setFieldValue('shippingType', 'profile');
    } else {
      setFieldValue('shippingType', 'standard');
      // Only set initial values if there are no existing values
      if (!values.standardShipping) {
        setFieldValue('standardShipping', resolveInitialStandardShipping(
          settings.productShipping, 
          config?.shopConfig?.shop.countryCode, 
          auth?.user?.seller?.address?.countryCode
        ));
      }
    }
  }, [setFieldValue, values.standardShipping, settings.productShipping, config?.shopConfig?.shop.countryCode, auth?.user?.seller?.address?.countryCode]);

  const handleViewEdit = React.useCallback(() => {
    if (values.deliveryProfileId) {
      deliveryProfile.openEditor({
        profileId: values.deliveryProfileId,
        onSave: (profileId) => {
          setFieldValue('deliveryProfileId', profileId);
        }
      });
    }
  }, [values.deliveryProfileId, deliveryProfile, setFieldValue]);

  const handleCreate = React.useCallback(() => {
    deliveryProfile.openEditor({
      onSave: (profileId) => {
        setFieldValue('deliveryProfileId', profileId);
      }
    });
  }, [deliveryProfile, setFieldValue]);

  const handleChoose = React.useCallback(() => {
    deliveryProfile.openSelector({
      onSelect: (profileId) => {
        setFieldValue('deliveryProfileId', profileId);
      }
    });
  }, [deliveryProfile, setFieldValue]);

  const handleChangeInternationalShipping = React.useCallback((value: boolean) => {
    setFieldValue('standardShipping.internationalRate', value ? 0 : null);
    setIsInternationalShipping(value);
  }, [setFieldValue]);

  React.useEffect(() => {
    if (!selectedShippingMethod && values.productType === ProductType.Physical) {
      handleShippingMethodChange('manual');
    }
  }, [values.productType, selectedShippingMethod, handleShippingMethodChange]);

  React.useEffect(() => {
    // Only fetch if:
    // 1. We have a profile ID
    // 2. Shipping type is 'profile'
    // 3. The current deliveryProfile is not set OR doesn't match the ID
    if (values.deliveryProfileId && 
        values.shippingType === 'profile' && 
        (!deliveryProfile.deliveryProfile || deliveryProfile.deliveryProfile.id !== values.deliveryProfileId)) {
      deliveryProfile.setCurrentProfile(values.deliveryProfileId);
    }
  }, [values.deliveryProfileId, values.shippingType]);

  // Early return checks after all hooks
  if (!settings || !settings.productShipping) {
    return null;
  }

  // Check for physical product type
  if (values.productType !== ProductType.Physical) {
    return null;
  }

  const { allowProfiles, inlineShippingOptions } = settings.productShipping;

  const manualShippingRates = <ShippingRateFields
    disabled={disabled}
    inlineShippingOptions={inlineShippingOptions}
    onChangeInternationalShipping={handleChangeInternationalShipping}
    isInternationalShipping={isInternationalShipping}
  />;

  const manualShippingRatesInRadio = (
    <ShippingRateFields
      disabled={disabled}
      inlineShippingOptions={inlineShippingOptions}
      isInternationalShipping={isInternationalShipping}
      onChangeInternationalShipping={handleChangeInternationalShipping}
    />
  );

  // The summary says which profile is applied; the buttons to change it appear
  // only on the selected row, where they are actionable.
  const deliveryProfileSummary = () => {
    // The design words this as a state, not a label: "No profile applied yet"
    // says what is true and implies what to do about it, where a "Not applied"
    // pill reads as a status somebody assigned. It shows on the unselected row
    // too, which is the point of a summary line — the row says what choosing
    // it would currently mean.
    const noneApplied = (
      <span className="text-xs text-muted-foreground">No profile applied yet</span>
    );

    if (selectedShippingMethod !== 'profile') {
      return values.deliveryProfileId && deliveryProfile.deliveryProfile ? (
        <Badge variant="neutral">{deliveryProfile.deliveryProfile.name}</Badge>
      ) : (
        noneApplied
      );
    }
    if (values.deliveryProfileId) {
      if (deliveryProfile.loading || !deliveryProfile.deliveryProfile) {
        return <DeliveryProfileLoading />;
      }
      return (
        <>
          <Badge variant="neutral">{deliveryProfile.deliveryProfile.name || 'Custom'}</Badge>
          <Badge variant="success">Applied</Badge>
          <DeliveryProfileApplied
            name={deliveryProfile.deliveryProfile.name}
            onChoose={handleChoose}
            onViewEdit={handleViewEdit}
            disabled={disabled}
          />
        </>
      );
    }
    return (
      <>
        {noneApplied}
        {!disabled && <DeliveryProfileNotSet onChoose={handleChoose} onCreate={handleCreate} />}
      </>
    );
  };

  const shippingOptions: RadioRowOption<string>[] = [
    {
      value: 'profile',
      label: 'Delivery profile',
      // What each option means, as drawn. The two labels are near-synonyms to
      // anyone who has not met a delivery profile before, and a radio group
      // whose options need explaining is one nobody can answer confidently.
      description: 'Use processing times and rates from a saved profile',
      summary: deliveryProfileSummary(),
      testId: 'shipping-option-profile',
    },
    {
      value: 'manual',
      label: 'Fixed rate',
      description: 'Flat rates set on this product',
      summary: fixedRateSummary(values.standardShipping),
      content: manualShippingRatesInRadio,
      testId: 'shipping-option-manual',
    },
  ];

  return (
    <SectionCard title="Shipping" testId="product-shipping-section">
      {allowProfiles ? (
        <RadioRows
          name="shipping-method"
          aria-label="Shipping options"
          value={selectedShippingMethod}
          disabled={disabled}
          onChange={handleShippingMethodChange}
          options={shippingOptions}
        />
      ) : (
        manualShippingRates
      )}
    </SectionCard>
  );
};
